import React from 'react';
import {
  BrowserRouter,
  Routes as Switch,
  Route, Navigate, Outlet, useNavigate, useOutletContext,
} from 'react-router-dom';
import { useQueryClient } from 'react-query';
import Login from '../screens/auth/login';
import Overview from '../screens/services/overview';
import Analytics from '../screens/services/analytics';
import Clients from '../screens/services/clients';
import ClientsDetail from '../screens/services/clients/detail';
import ClientDetailInvoices from '../screens/services/clients/detail/invoices';
import ClientDetailCoffeeInvoices from '../screens/services/clients/detail/coffee-invoices';
import ClientDetailCoffeeInvoiceDetail from '../screens/services/clients/detail/view-coffee-invoice';
import VendingMachine from '../screens/services/vending-machine';
import Products from '../screens/services/products';
import AddProduct from '../screens/services/products/add';
import Routes from '../screens/services/routes';
import Warehouse from '../screens/services/warehouse';
import UserManagement from '../screens/services/user-management';
import UsersAddUser from '../screens/services/user-management/add';
import CustomerSupport from '../screens/services/customer-support';
import Registration from '../screens/auth/signup';
import ForgotPassword from '../screens/auth/forgot-password';
import UpdatePassword from '../screens/auth/update-password';
import DetailRoute from '../screens/services/routes/route/id';
import Sidebar from '../components/layout/sidebar';
import RoutesAddRoute from '../screens/services/routes/route/add/index';
import ScheduledRestocking from '../screens/services/routes/scheduled/re-stocking';
import EditClientDetail from '../screens/services/routes/route/edit-client';
import RoutesAddSchedule from '../screens/services/routes/scheduled/add';
import AddVendingMachine from '../screens/services/vending-machine/add';
import RenderTableDexData from '../screens/services/vending-machine/dex';
import VMPlanogram from '../screens/services/vending-machine/planogram';
import ModelManagement from '../screens/services/vending-machine/model-management';
import ClientsAddContact from '../screens/services/clients/detail/contact';
import ClientsAddPartner from '../screens/services/clients/detail/partner';
import ClientsDetailAddHeader from '../screens/services/clients/utils/add-header';
import AddClient from '../screens/services/clients/add';
import Partners from '../screens/services/partners';
import DetailUser from '../screens/services/user-management/detail';
import DetailPartner from '../screens/services/partners/id';
import EditUserProfile from '../screens/services/user-profile';
import PartnersContactPerson from '../screens/services/partners/add/contact-person';
import PartnersPartner from '../screens/services/partners/add/partner';
import AddPartnersDetail from '../screens/services/partners/add';
import AddModelConfiguration from '../screens/services/vending-machine/model-management/add';
import ModelManagementDetail from '../screens/services/vending-machine/model-management/detail';
import AddInvoice from '../screens/services/customer-support/add-invoice';
import AddRefund from '../screens/services/customer-support/add-refund';
import AddWarehouse from '../screens/services/warehouse/add';
import AddService from '../screens/services/customer-support/add-service';
import EditService from '../screens/services/customer-support/edit-service';
import AisleLevelDetails from '../screens/services/warehouse/aisle/detail';
import Dummy from '../screens/dummy';
import ViewInvoice from '../screens/services/customer-support/view-invoice';
import Upload from '../screens/services/upload';
import VendingPickingDashboard from '../screens/services/picking';
import RouteDriverProgress from '../screens/services/driver';

function RequireAuth() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const tokenStorage = sessionStorage.getItem('token');
    const login = sessionStorage.getItem('isLoggedIn');

    if (!tokenStorage || login !== 'true') {
      sessionStorage.clear();
      navigate('/login', { replace: true });
    }
  }, []);

  return <Outlet />;
}

function AlreadyAuth() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const tokenStorage = sessionStorage.getItem('token');
    const login = sessionStorage.getItem('isLoggedIn');

    if (tokenStorage && login) {
      navigate('/overview', { replace: true });
    }
  }, []);

  return <Outlet />;
}

const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

function Wrapper(props: { route: string }) {
  const { route } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [, setCurrentPath]: [string, React.Dispatch<string>] = useOutletContext();
  React.useEffect(() => {
    const tokenStorage = sessionStorage.getItem('token');
    const login = sessionStorage.getItem('isLoggedIn');
    const decodedJwt = parseJwt(tokenStorage ?? '');

    if (!tokenStorage || login !== 'true' || (decodedJwt.exp * 1000 < Date.now())) {
      queryClient.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
    }
    setCurrentPath(route);
  }, [route]);
  return <Outlet />;
}

function PageRouter(): React.ReactElement {
  return (
    <BrowserRouter>
      <Switch>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route element={<RequireAuth />}>
          <Route element={<Sidebar />}>
            <Route path="user-profile" element={<EditUserProfile />} />

            <Route element={<Wrapper route="overview" />}>
              <Route path="overview" element={<Overview />} />
            </Route>

            <Route element={<Wrapper route="analytics" />}>
              <Route path="analytics" element={<Analytics />} />
            </Route>

            <Route element={<Wrapper route="clients" />}>
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:clientId" element={<ClientsDetail />} />
              <Route path="clients/add" element={<AddClient />} />
              <Route element={<ClientsDetailAddHeader />}>
                {/* Must have :clientId for header fetching */}
                <Route path="clients/invoice/:clientId" element={<ClientDetailInvoices />} />
                <Route path="clients/coffee-invoice/:clientId" element={<ClientDetailCoffeeInvoices />} />
                <Route path="clients/view-coffee-invoice/:clientId/:invoiceId" element={<ClientDetailCoffeeInvoiceDetail />} />
                <Route path="clients/contact/:clientId" element={<ClientsAddContact />} />
                <Route path="clients/contact/:clientId/:contactId" element={<ClientsAddContact />} />
                <Route path="clients/partner/:clientId" element={<ClientsAddPartner />} />
                <Route path="clients/partner/:clientId/:partnerId" element={<ClientsAddPartner />} />
                <Route path="clients/add/:clientId" element={<AddClient />} />
              </Route>
            </Route>

            <Route element={<Wrapper route="partners" />}>
              <Route path="partners" element={<Partners />} />
              <Route path="partners/:partnerId" element={<DetailPartner />} />
              <Route path="partners/add/:partnerId" element={<AddPartnersDetail />} />
              <Route path="partners/add/" element={<AddPartnersDetail />} />
              <Route path="partners/edit/:partnerId/:contactId" element={<PartnersContactPerson />} />
              <Route path="partners/edit/:partnerId/" element={<PartnersPartner />} />
            </Route>

            <Route element={<Wrapper route="vending-machine" />}>
              <Route path="vending-machine" element={<VendingMachine />} />
              <Route path="vending-machine/model-management" element={<ModelManagement />} />
              <Route path="vending-machine/model-management/:modelId" element={<ModelManagementDetail />} />
              <Route path="vending-machine/model-management/add" element={<AddModelConfiguration />} />
              <Route path="vending-machine/model-management/add/:modelId" element={<AddModelConfiguration />} />
              <Route path="vending-machine/add" element={<AddVendingMachine />} />
              <Route path="vending-machine/add/:clientName" element={<AddVendingMachine />} />
              <Route path="vending-machine/add/:clientName/:vmId" element={<AddVendingMachine />} />
              <Route path="vending-machine/planogram/:vmId" element={<VMPlanogram />} />
              <Route path="vending-machine/dex/:vmId" element={<RenderTableDexData />} />
            </Route>

            <Route element={<Wrapper route="products" />}>
              <Route path="products" element={<Products />} />
              <Route path="products/add" element={<AddProduct />} />
              <Route path="products/add/:productId" element={<AddProduct />} />
            </Route>

            <Route element={<Wrapper route="routes" />}>
              <Route path="routes" element={<Routes />} />
              <Route path="routes/:currentScreen" element={<Routes />} />
              {/* currentScreen must only 'route' or 'schedule' */}
              <Route path="routes/route/:id" element={<DetailRoute />} />
              <Route path="routes/route/add" element={<RoutesAddRoute />} />
              <Route path="routes/route/add/:id" element={<RoutesAddRoute />} />
              <Route path="routes/route/client/:routeId/:clientId" element={<EditClientDetail />} />
              <Route path="routes/schedule/add" element={<RoutesAddSchedule />} />
              <Route path="routes/schedule/add/:id" element={<RoutesAddSchedule />} />
              <Route path="routes/schedule/vm/re-stocking" element={<ScheduledRestocking />} />
              <Route path="routes/schedule/vm/re-stocking/:id" element={<ScheduledRestocking />} />
            </Route>

            <Route element={<Wrapper route="warehouse" />}>
              <Route path="warehouse" element={<Warehouse />} />
              <Route path="warehouse/add" element={<AddWarehouse />} />
              <Route path="warehouse/add/:id" element={<AddWarehouse />} />
              <Route path="warehouse/aisle/:warehouseId/:aisleId/:aisleName/:level" element={<AisleLevelDetails />} />
              <Route path="warehouse/aisle/:warehouseId/:aisleId/:aisleName/:level/:search" element={<AisleLevelDetails />} />
            </Route>

            <Route element={<Wrapper route="customer-support" />}>
              <Route path="customer-support" element={<CustomerSupport />} />
              <Route path="customer-support/:currentScreen" element={<CustomerSupport />} />
              <Route path="customer-support/add-invoice/:ticketId" element={<AddInvoice />} />
              <Route path="customer-support/invoice" element={<ViewInvoice />} />
              <Route path="customer-support/refund/add" element={<AddRefund />} />
              <Route path="customer-support/service/add" element={<AddService />} />
              <Route path="customer-support/service/add/:ticketId" element={<EditService />} />
            </Route>

            <Route element={<Wrapper route="user-management" />}>
              <Route path="user-management" element={<UserManagement />} />
              {/* currentScreen must only 'active' or 'deleted' */}
              <Route path="user-management/:currentScreen" element={<UserManagement />} />
              <Route path="user-management/detail/:currentScreen/:id" element={<DetailUser />} />
              <Route path="user-management/user/add" element={<UsersAddUser />} />
              <Route path="user-management/user/add/:userId" element={<UsersAddUser />} />
            </Route>

            <Route element={<Wrapper route="coffee-machine" />}>
              <Route path="coffee-machine" element={<VendingMachine />} />
              <Route path="coffee-machine/add" element={<AddVendingMachine />} />
              <Route path="coffee-machine/add/:clientName" element={<AddVendingMachine />} />
              <Route path="coffee-machine/add/:clientName/:vmId" element={<AddVendingMachine />} />
              <Route path="coffee-machine/planogram/:vmId" element={<VMPlanogram />} />
            </Route>

            <Route element={<Wrapper route="wholesale" />}>
              <Route path="wholesale" element={<VendingMachine />} />
              <Route path="wholesale/add" element={<AddVendingMachine />} />
              <Route path="wholesale/add/:clientName" element={<AddVendingMachine />} />
              <Route path="wholesale/add/:clientName/:vmId" element={<AddVendingMachine />} />
              <Route path="wholesale/planogram/:vmId" element={<VMPlanogram />} />
            </Route>

            <Route element={<Wrapper route="upload" />}>
              <Route path="upload" element={<Upload />} />
            </Route>

            <Route element={<Wrapper route="picking" />}>
              <Route path="picking" element={<VendingPickingDashboard />} />
            </Route>

            <Route element={<Wrapper route="driver" />}>
              <Route path="driver" element={<RouteDriverProgress />} />
            </Route>

            <Route path="dummy" element={<Dummy />} />

          </Route>
        </Route>
        <Route element={<AlreadyAuth />}>
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="update-pass" element={<UpdatePassword />} />
          <Route path="signup" element={<Registration />} />
        </Route>
      </Switch>
    </BrowserRouter>
  );
}

export default PageRouter;
